import React, { useState, useEffect } from "react";
import { Button, Box } from "@mui/material";

interface VariableValues {
  A: number;
  B: number;
  C: number;
}

interface VariableRelationship {
  variable: "A" | "B" | "C";
  type: "direct" | "derived";
  value?: number; // for direct
  baseVar?: "A" | "B" | "C"; // for derived
  modifier?: number; // for derived (+2, -1, etc.)
}

interface VariableMemoryGameProps {
  onGameEnd: (score: number, values: VariableValues) => void;
}

function getRandomValues(): {
  values: VariableValues;
  relationships: VariableRelationship[];
} {
  const variables: ("A" | "B" | "C")[] = ["A", "B", "C"];

  // Determine number of derived variables (1 or 2 for advanced difficulty)
  const numDerived = Math.random() < 0.5 ? 1 : 2;

  // Shuffle and select which variables are derived
  const shuffled = shuffleArray([...variables]);
  const derivedVars = shuffled.slice(0, numDerived);
  const directVars = shuffled.slice(numDerived);

  // Initialize relationships array
  const relationships: VariableRelationship[] = [];
  const values: VariableValues = { A: 0, B: 0, C: 0 };

  // Step 1: Assign direct values first
  directVars.forEach((varName) => {
    const value = Math.floor(Math.random() * 9) + 1;
    values[varName] = value;
    relationships.push({
      variable: varName,
      type: "direct",
      value: value,
    });
  });

  // Step 2: Assign derived values
  // To avoid circular dependencies, derived variables can only reference direct variables
  // or previously assigned derived variables
  const assignedVars = [...directVars];

  derivedVars.forEach((varName) => {
    // Choose a base variable from already assigned variables
    const baseVar =
      assignedVars[Math.floor(Math.random() * assignedVars.length)];
    const baseValue = values[baseVar];

    // Choose modifier: ±1, ±2, ±3
    const possibleModifiers: number[] = [];
    for (const mod of [1, 2, 3, -1, -2, -3]) {
      if (baseValue + mod >= 1 && baseValue + mod <= 9) {
        possibleModifiers.push(mod);
      }
    }

    const modifier =
      possibleModifiers[Math.floor(Math.random() * possibleModifiers.length)];
    const derivedValue = baseValue + modifier;

    values[varName] = derivedValue;
    relationships.push({
      variable: varName,
      type: "derived",
      baseVar: baseVar,
      modifier: modifier,
    });

    // Add to assigned vars so next derived var can potentially reference this one
    assignedVars.push(varName);
  });

  return { values, relationships };
}

function shuffleArray<T>(array: T[]): T[] {
  return array.sort(() => Math.random() - 0.5);
}

function getDisplayValue(relationship: VariableRelationship): string {
  if (relationship.type === "direct") {
    return relationship.value!.toString();
  } else {
    const sign = relationship.modifier! > 0 ? "+" : "-";
    const absModifier = Math.abs(relationship.modifier!);
    return `${relationship.baseVar} ${sign} ${absModifier}`;
  }
}

const AdvancedVariableGameRounds: React.FC<VariableMemoryGameProps> = ({
  onGameEnd,
}) => {
  const [valuesData] = useState(() => getRandomValues());
  const { values, relationships } = valuesData;

  // Create a map for easy lookup of display values
  const displayMap = relationships.reduce(
    (acc, rel) => {
      acc[rel.variable] = getDisplayValue(rel);
      return acc;
    },
    {} as Record<"A" | "B" | "C", string>,
  );

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showQuestion, setShowQuestion] = useState<boolean>(false);
  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const [score, setScore] = useState<number>(0);
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [gameFinished, setGameFinished] = useState<boolean>(false);
  const [shuffledVariables, setShuffledVariables] = useState<
    ("A" | "B" | "C")[]
  >(shuffleArray(["A", "B", "C"]));

  useEffect(() => {
    if (gameStarted && currentStep < 3) {
      const timer = setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    } else if (gameStarted && currentStep === 3) {
      setTimeout(() => {
        setShowQuestion(true);
      }, 1000);
    }
  }, [gameStarted, currentStep]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (showQuestion && event.key >= "1" && event.key <= "9") {
        const currentVariable = shuffledVariables[questionIndex];
        console.log(
          `Key pressed: ${event.key}, Expected: ${values[currentVariable]}`,
        );

        setScore((prevScore) => {
          return parseInt(event.key, 10) === values[currentVariable]
            ? prevScore + 1
            : prevScore;
        });

        if (questionIndex + 1 >= shuffledVariables.length) {
          setShowQuestion(false);
          setGameFinished(true);
          setGameStarted(false);
          setTimeout(
            () =>
              onGameEnd(
                score +
                  (parseInt(event.key, 10) === values[currentVariable] ? 1 : 0),
                values,
              ),
            0,
          );
        } else {
          setQuestionIndex((prev) => prev + 1);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [
    questionIndex,
    values,
    showQuestion,
    onGameEnd,
    score,
    shuffledVariables,
  ]);

  const startGame = () => {
    setGameStarted(true);
    setGameFinished(false);
    setCurrentStep(0);
    setShowQuestion(false);
    setQuestionIndex(0);
    setScore(0);
    setShuffledVariables(shuffleArray(["A", "B", "C"]));
  };

  return (
    <div
      id="numberDisplay"
      style={{
        fontSize: "48px",
        fontWeight: "bold",
        textAlign: "center",
        marginTop: "20vh",
      }}
    >
      <Box>
        {gameStarted && currentStep === 0 && <div>A = {displayMap.A}</div>}
        {gameStarted && currentStep === 1 && <div>B = {displayMap.B}</div>}
        {gameStarted && currentStep === 2 && <div>C = {displayMap.C}</div>}
      </Box>
      {!gameStarted && !gameFinished ? (
        <Box sx={{ textAlign: "center", marginTop: "1em" }}>
          <Button variant="outlined" onClick={startGame} color="inherit">
            {"Lets Play"}
          </Button>
        </Box>
      ) : showQuestion && questionIndex < shuffledVariables.length ? (
        <h3>{shuffledVariables[questionIndex]} = ?</h3>
      ) : null}
    </div>
  );
};

export default AdvancedVariableGameRounds;
